import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, query } from 'firebase/firestore';
import { addDays, eachDayOfInterval, endOfDay, format as formatDate, fromUnixTime, getUnixTime, min as minDate } from 'date-fns';
import { useAsync } from 'react-use';
import { groupBy, keyBy, orderBy, uniqBy } from 'lodash';
import classnames from 'classnames';

import { db, functions } from '../../firebase';
import { fullPathWithParams } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../AdminPage';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import { SubscriptionStatusBadge, stripeCustomerUrl } from './AdminTenants';

const columnGroups = [
  {
    label: 'トライアル',
    columns: [
      { value: 'trial', shortLabel: '開始', label: 'トライアル開始' },
      { value: 'trialCanceled', shortLabel: '解約', label: 'トライアル中の解約' },
      { value: 'trialBalance', shortLabel: '数', label: 'トライアル数', isBalance: true },
    ],
  },
  {
    label: '有料',
    columns: [
      { value: 'new', shortLabel: '新規', label: '新規（有料開始）' },
      { value: 'paidCanceled', shortLabel: '解約', label: '有料の解約' },
      { value: 'paidBalance', shortLabel: '数', label: '有料数', isBalance: true },
    ],
  },
];
const columns = columnGroups.flatMap(_ => _.columns);
const columnsByValue = keyBy(columns, 'value');

const liveAt = (subscription, at) => subscription.created <= at && (!subscription.endedAt || subscription.endedAt > at);

const paidStartOf = (subscription, now) => {
  const { created, trialEnd, endedAt } = subscription;
  if (!trialEnd) return created;
  const isConverted = trialEnd <= now && !(endedAt && endedAt <= trialEnd);
  return isConverted ? trialEnd : null;
};

export default function AdminSubscriptions() {
  return (
    <AdminPage>
      <PageContent />
    </AdminPage>
  );
}

function PageContent() {
  const location = useLocation();
  const queryParams = useQueryParams();
  const { date: selectedDate, type: selectedType } = queryParams;
  const defaultDateRange = [addDays(new Date(), -30), new Date()];
  const [startDateString, endDateString] = queryParams.dateRange || defaultDateRange.map(_ => formatDate(_, 'yyyy-MM-dd'));

  const tenants = useCollectionSubscription(query(collection(db, 'tenants')));
  const users = useCollectionSubscription(query(collection(db, 'users')));
  const tenantsByStripeCustomerId = keyBy(tenants.filter(_ => _.stripeCustomerId), 'stripeCustomerId');
  const usersGroupedByTenantId = groupBy(users, 'tenantId');

  const { loading, error, value: subscriptions = [] } = useAsync(async () => {
    const { data } = await httpsCallable(functions, 'adminListSubscriptions')();
    return data;
  }, []);

  const events = useMemo(() => {
    const now = Date.now() / 1000;
    const subscriptionsGroupedByCustomerId = groupBy(subscriptions, 'customerId');
    return subscriptions.flatMap((subscription) => {
      const tenant = tenantsByStripeCustomerId[subscription.customerId];
      if (!tenant) return [];

      // 二重契約や再契約の重なりで、契約が続いているのに増減として数えないように
      const othersLiveAt = at => subscriptionsGroupedByCustomerId[subscription.customerId].some(_ => _.id !== subscription.id && liveAt(_, at));
      return [
        { type: 'trial', at: subscription.trialStart },
        { type: 'new', at: paidStartOf(subscription, now) },
        { type: subscription.trialEnd && subscription.endedAt <= subscription.trialEnd ? 'trialCanceled' : 'paidCanceled', at: subscription.endedAt },
      ]
        .filter(_ => _.at && !othersLiveAt(_.at))
        .map(_ => ({ ..._, subscription, tenant, date: formatDate(fromUnixTime(_.at), 'yyyy-MM-dd') }));
    });
  }, [subscriptions, tenants]);

  const dates = useMemo(() => startDateString && endDateString && startDateString <= endDateString
    ? orderBy(eachDayOfInterval({ start: new Date(startDateString), end: new Date(endDateString) }).map(_ => formatDate(_, 'yyyy-MM-dd')), _ => _, 'desc')
    : [], [startDateString, endDateString]);

  const balances = useMemo(() => {
    const now = new Date();
    const tenantSubscriptions = subscriptions.filter(_ => tenantsByStripeCustomerId[_.customerId]);
    return dates.flatMap((date) => {
      const at = getUnixTime(minDate([endOfDay(new Date(date)), now]));
      const items = tenantSubscriptions
        .filter(_ => liveAt(_, at))
        .map((subscription) => {
          const paidStart = paidStartOf(subscription, at);
          const isCancelScheduled = !!subscription.canceledAt && subscription.canceledAt <= at;
          return paidStart
            ? { type: 'paidBalance', at: paidStart, subscription, isCancelScheduled }
            : { type: 'trialBalance', at: subscription.trialStart, subscription, isCancelScheduled };
        });
      // 二重契約は1テナント1件として数える
      return uniqBy(orderBy(items, _ => _.type === 'paidBalance', 'desc'), _ => _.subscription.customerId)
        .map(_ => ({ ..._, tenant: tenantsByStripeCustomerId[_.subscription.customerId], date }));
    });
  }, [subscriptions, tenants, dates]);

  const eventsInRange = events.filter(_ => _.date >= startDateString && _.date <= endDateString);
  const itemsGroupedByDate = groupBy([...balances, ...eventsInRange], 'date');
  const selectedItems = selectedType
    ? orderBy((selectedDate ? itemsGroupedByDate[selectedDate] || [] : eventsInRange).filter(_ => _.type === selectedType), 'at', 'desc')
    : [];
  const isBalanceSelected = !!columnsByValue[selectedType]?.isBalance;

  const CountCell = ({ date, type, events }) => {
    const count = events.filter(_ => _.type === type).length;
    const isSelected = selectedType === type && (selectedDate || null) === (date || null);
    return (
      <td className={classnames('text-end', { 'table-primary': isSelected })}>
        {count > 0 ? (
          <Link to={fullPathWithParams({ date: date || undefined, type }, location)} className="fw-bold">
            {count}
          </Link>
        ) : (
          <span className="text-muted">0</span>
        )}
      </td>
    );
  };

  return (
    <div className="admin-subscriptions container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>課金状況</h4>
        </div>
        <div className="mb-3">
          <QueryDateRangeSelector
            paramName="dateRange"
            label="期間"
            defaultValue={defaultDateRange}
          />
        </div>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status" />
          </div>
        ) : error ? (
          <div className="alert alert-danger">Stripeからの取得に失敗しました: {error.message}</div>
        ) : (
          <div className="row">
            <div className="col-lg-5">
              <table className="table table-sm table-bordered">
                <thead className="table-light text-center text-nowrap">
                  <tr>
                    <th rowSpan={2} className="align-middle">日付</th>
                    {columnGroups.map(_ => <th key={_.label} colSpan={_.columns.length}>{_.label}</th>)}
                  </tr>
                  <tr>
                    {columns.map(_ => <th key={_.value}>{_.shortLabel}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr className="fw-bold">
                    <td>期間合計</td>
                    {columns.map(_ => _.isBalance ? <td key={_.value} /> : <CountCell key={_.value} type={_.value} events={eventsInRange} />)}
                  </tr>
                  {dates.map(date => (
                    <tr key={date}>
                      <td className="text-nowrap">{formatDate(new Date(date), 'yyyy/MM/dd')}</td>
                      {columns.map(_ => <CountCell key={_.value} date={date} type={_.value} events={itemsGroupedByDate[date] || []} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="col-lg-7">
              {selectedType && (
                <>
                  <h5 className="mb-3">
                    {selectedDate ? formatDate(new Date(selectedDate), 'yyyy/MM/dd') : '期間合計'} の{columnsByValue[selectedType]?.label}: {selectedItems.length}件
                  </h5>
                  <div className="overflow-auto">
                    <table className="table table-sm">
                      <thead className="table-light text-nowrap">
                        <tr>
                          <th>{isBalanceSelected ? '開始日時' : '日時'}</th>
                          <th>テナント名</th>
                          <th>ユーザーメールアドレス</th>
                          <th>現在のサブスク状況</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedItems.map(({ subscription, tenant, at, isCancelScheduled }) => (
                          <tr key={subscription.id}>
                            <td className="text-nowrap">
                              {formatDate(fromUnixTime(at), 'yyyy/MM/dd HH:mm')}
                            </td>
                            <td>{tenant.name}</td>
                            <td>{usersGroupedByTenantId[tenant.id]?.map(_ => _.email).join(', ')}</td>
                            <td className="text-nowrap">
                              <SubscriptionStatusBadge status={tenant.subscriptionStatus} />
                              {isCancelScheduled && <span className="badge bg-warning text-dark ms-1">解約予約</span>}
                            </td>
                            <td>
                              <a className="small text-nowrap" href={stripeCustomerUrl(subscription.customerId)} target="_blank" rel="noreferrer">
                                Stripeで見る
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
